'use client';

import type {
  IconButtonProps,
  MenuItemProps,
  DividerProps,
  PopperPlacementType,
} from '@mui/material';
import {
  Box,
  ClickAwayListener,
  Fade,
  Paper,
  Popper,
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Stack,
} from '@mui/material';
import classnames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';

import { Icon } from '@/components/shared/icon';
import { Link } from '@/components/shared/link';
import { Loader } from '@/components/shared/loader';

import { GroupTitle } from './groupTitle';

import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';

type IsDisabledResult = boolean | { disabled: boolean; tooltip?: string };

export interface OptionMenuItemType {
  id: string;
  groupHeader?: boolean;
  divider?: boolean;
  href?: string;
  dividerProps?: DividerProps;
  icon?: ReactNode;
  text?: ReactNode;
  rightContent?: ReactNode;
  nonInteractive?: boolean;
  menuItemProps?: MenuItemProps;
  predicate?: () => boolean;
  isDisabled?: () => IsDisabledResult | Promise<IsDisabledResult>;
  isAuthorized?: boolean;
  isFeatureSupported?: boolean;
  subMenu?: OptionType[];
  disabledTooltip?: string;
}

export type OptionType = string | OptionMenuItemType;

interface OptionsMenuType {
  icon?: ReactNode;
  iconClassName?: string;
  options: OptionType[];
  placement?: PopperPlacementType;
  iconButtonProps?: IconButtonProps;
  'data-testid'?: string;
  onToggle?: (open: boolean) => void;
  onOptionSelected?: (option: OptionType) => void;
  // Drop the open/close fade so the menu mounts and unmounts synchronously. Use
  // when selecting an option shifts the menu's anchor: with the fade, the
  // closing menu would slide to follow the moved anchor before disappearing.
  disableTransition?: boolean;
  Component?: React.ElementType;
  componentProps?: Record<string, unknown>;
  popperClassName?: string;
  // The menu renders where it sits in the DOM, so any ancestor that hides its
  // overflow crops it — which a caller inside a scroll container only sees once
  // the container is short enough for the menu to reach past it. Pass false
  // there to lift the menu out to the body instead.
  disablePortal?: boolean;
}

const InnerMenu = (
  props: OptionsMenuType & {
    open: boolean;
    placement: PopperPlacementType;
    closeMenu: () => void;
    anchorEl: HTMLElement | null;
  }
) => {
  const {
    onOptionSelected,
    disableTransition,
    closeMenu,
    open,
    options,
    anchorEl,
    placement,
    popperClassName,
    disablePortal = true,
  } = props;
  const [subMenuOpen, setSubMenuOpen] = useState<number>();
  const [disabledStates, setDisabledStates] = useState<
    Record<string, { disabled: boolean; tooltip?: string }>
  >({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) {
      return;
    }

    const optionsWithDynamicDisabled = options.filter(
      (o): o is OptionMenuItemType =>
        typeof o === 'object' &&
        'isDisabled' in o &&
        typeof o.isDisabled === 'function'
    );

    if (optionsWithDynamicDisabled.length === 0) {
      return;
    }

    setLoadingIds(new Set(optionsWithDynamicDisabled.map((o) => o.id)));

    void Promise.all(
      optionsWithDynamicDisabled.map(async (option) => {
        const raw = await option.isDisabled!();
        const resolved = typeof raw === 'boolean' ? { disabled: raw } : raw;
        setDisabledStates((prev) => ({ ...prev, [option.id]: resolved }));
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(option.id);
          return next;
        });
      })
    );
  }, [open, options]);

  const handleClose = useCallback(
    (event: Event | SyntheticEvent) => {
      if (anchorEl?.contains(event.target as HTMLElement)) {
        return;
      }
      setSubMenuOpen(undefined);
      closeMenu();
    },
    [anchorEl, closeMenu]
  );
  // Closes without handleClose's anchor guard: that guard is there so a click on
  // the trigger button doesn't fight the toggle, but a sub-menu is anchored to
  // its parent row, so the guard would swallow every nested selection.
  const handleSelect = useCallback(
    (option: OptionType) => {
      if (onOptionSelected) {
        onOptionSelected(option);
      }
      setSubMenuOpen(undefined);
      closeMenu();
    },
    [onOptionSelected, closeMenu]
  );

  const filteredOptions = options.filter((o) =>
    typeof o === 'object' && 'predicate' in o ? o.predicate?.() : true
  );

  const menuContent = (
    <Paper
      sx={{
        boxShadow: 'var(--mui-customShadows-lg)',
      }}
    >
      <ClickAwayListener onClickAway={handleClose}>
        <MenuList autoFocusItem={open} onClick={(e) => e.stopPropagation()}>
          {filteredOptions.map((option: OptionType, index: number) => {
            if (typeof option === 'string') {
              return (
                <MenuItem
                  key={option}
                  data-testid={option}
                  sx={{ minWidth: '150px' }}
                  onClick={() => {
                    handleSelect(option);
                  }}
                >
                  {option}
                </MenuItem>
              );
            } else if ('divider' in option) {
              const previousOption = filteredOptions[index - 1];
              if (
                index === 0 ||
                index === filteredOptions.length - 1 ||
                (typeof previousOption !== 'string' &&
                  'divider' in previousOption)
              ) {
                return null;
              }
              if (!option.divider) {
                return null;
              }
              return (
                <Divider
                  key={option.id}
                  {...option.dividerProps}
                  className='my-[8px]'
                />
              );
            } else if ('groupHeader' in option) {
              return <GroupTitle key={option.id} title={option.text} />;
            } else if (option.nonInteractive) {
              return (
                <Stack
                  key={option.id}
                  data-testid={option.id}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ padding: '6px 16px', cursor: 'default' }}
                >
                  <Option
                    icon={option.icon}
                    text={option.text}
                    rightContent={option.rightContent}
                  />
                </Stack>
              );
            } else {
              return (
                <SubMenuInnerOption
                  key={option.id}
                  option={option}
                  subMenuOpen={subMenuOpen}
                  index={index}
                  setSubMenuOpen={setSubMenuOpen}
                  handleSelect={handleSelect}
                  props={props}
                  dynamicDisabledState={disabledStates[option.id]}
                  isLoadingDisabled={loadingIds.has(option.id)}
                />
              );
            }
          })}
        </MenuList>
      </ClickAwayListener>
    </Paper>
  );

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition={!disableTransition}
      disablePortal={disablePortal}
      className={popperClassName}
      sx={{ zIndex: 5 }}
    >
      {disableTransition
        ? menuContent
        : ({ TransitionProps }) => (
            <Fade {...TransitionProps}>{menuContent}</Fade>
          )}
    </Popper>
  );
};

const SubMenuInnerOption = ({
  option,
  subMenuOpen,
  index,
  setSubMenuOpen,
  handleSelect,
  props,
  dynamicDisabledState,
  isLoadingDisabled,
}: {
  option: OptionMenuItemType;
  subMenuOpen: number | undefined;
  index: number;
  setSubMenuOpen: (index: number | undefined) => void;
  handleSelect: (option: OptionType) => void;
  props: OptionsMenuType & {
    open: boolean;
    placement: PopperPlacementType;
    closeMenu: () => void;
    anchorEl: HTMLElement | null;
  };
  dynamicDisabledState?: { disabled: boolean; tooltip?: string };
  isLoadingDisabled?: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const isDisabled =
    option.menuItemProps?.disabled ||
    isLoadingDisabled ||
    dynamicDisabledState?.disabled;

  const tooltipTitle = isDisabled
    ? (dynamicDisabledState?.tooltip ?? option.disabledTooltip)
    : undefined;

  const linkProps = option.href ? { component: Link, href: option.href } : {};

  return (
    <AuthorizationTooltip
      isAuthorized={option.isAuthorized}
      isFeatureSupported={option.isFeatureSupported}
      title={tooltipTitle}
      key={option.id}
    >
      <MenuItem
        {...option.menuItemProps}
        {...linkProps}
        disabled={isDisabled}
        ref={setAnchorEl}
        onMouseEnter={(e) => {
          setSubMenuOpen(index);
          option.menuItemProps?.onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          setSubMenuOpen(undefined);
          option.menuItemProps?.onMouseLeave?.(e);
        }}
        onClick={(e) => {
          // A row that owns a fly-out opens it; only a leaf is a selection.
          // Without this a click (rather than a hover) on the row would dismiss
          // the whole menu, and the user would end up having selected nothing.
          if (option.subMenu) {
            setSubMenuOpen(index);
          } else {
            handleSelect(option);
          }
          option.menuItemProps?.onClick?.(e);
        }}
        data-testid={option.id}
      >
        <Option
          icon={option.icon}
          text={option.text}
          rightContent={option.rightContent}
          isSubMenu={!!option.subMenu}
          isLoadingDisabled={isLoadingDisabled}
        />
        {option.subMenu && (
          <InnerMenu
            {...props}
            anchorEl={anchorEl}
            options={option.subMenu}
            // Offsets in popperClassName are tuned to the root menu's trigger and
            // would displace a fly-out anchored to a row. Descendant-selector
            // styling in it still reaches here: disablePortal keeps this popper
            // inside the root one, which is why it is pinned rather than
            // inherited — a root that lifts itself to the body would otherwise
            // take its fly-outs out of reach of that styling too.
            popperClassName={undefined}
            disablePortal
            placement='right-start'
            open={subMenuOpen === index}
            // Chains to the level above so picking a nested option dismisses the
            // whole stack, not just the fly-out it was picked from.
            closeMenu={() => {
              setSubMenuOpen(undefined);
              props.closeMenu();
            }}
          />
        )}
      </MenuItem>
    </AuthorizationTooltip>
  );
};

const OptionMenu = (props: OptionsMenuType) => {
  const {
    icon = 'material-symbols-more-horiz',
    iconClassName,
    iconButtonProps,
    onToggle,
    Component,
    componentProps,
    placement = 'bottom-end',
    'data-testid': dataTestId = 'option-menu-button',
  } = props;
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
    onToggle?.(!open);
  };

  if (
    props.options.length === 0 ||
    props.options.every(
      (obj) =>
        typeof obj === 'object' &&
        (('predicate' in obj && !obj.predicate?.()) || obj.divider)
    )
  ) {
    return null;
  }

  // Cast away `React.ElementType`'s prop union so `ref`/`onClick` don't
  // collapse to `never` — global JSX augmentations from @react-three/fiber
  // (pulled in transitively via reagraph) widen IntrinsicElements and break
  // intersection-based prop inference here.
  const ButtonComponent = Component as React.ComponentType<
    Record<string, unknown> & {
      ref?: React.Ref<HTMLElement>;
      onClick?: React.MouseEventHandler<HTMLButtonElement>;
    }
  >;

  return (
    <>
      {Component && (
        <ButtonComponent
          {...componentProps}
          data-testid={dataTestId}
          ref={setAnchorEl}
          onClick={handleToggle}
        />
      )}
      {!Component && (
        <IconButton
          disableRipple
          ref={setAnchorEl}
          data-testid={dataTestId}
          onClick={handleToggle}
          {...iconButtonProps}
        >
          {typeof icon === 'string' ? (
            <i
              className={classnames(
                icon,
                'text-(--mui-palette-text-primary)',
                iconClassName
              )}
            />
          ) : (
            icon
          )}
        </IconButton>
      )}
      <InnerMenu
        {...props}
        placement={placement}
        open={open}
        closeMenu={() => {
          setOpen(false);
          onToggle?.(false);
        }}
        anchorEl={anchorEl}
      />
    </>
  );
};

export const menuItemStyles = (isSelected: boolean) => {
  return {
    height: '36px',
    padding: '4px 16px 4px 16px',
    borderRadius: 0,

    ...(isSelected
      ? {
          backgroundColor: 'var(--mui-palette-primary-main)',
          color: 'var(--mui-palette-background-default)',
        }
      : {}),

    '&:hover': {
      backgroundColor: isSelected
        ? 'var(--mui-palette-primary-main)'
        : 'var(--mui-palette-primary-dark)',
    },
  };
};

export { OptionMenu };

const Option = ({
  icon,
  text,
  rightContent,
  isSubMenu,
  isLoadingDisabled,
}: {
  icon?: ReactNode;
  text: ReactNode;
  rightContent?: ReactNode;
  isSubMenu?: boolean;
  isLoadingDisabled?: boolean;
}) => {
  return (
    <Stack
      direction='row'
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: '4px',
      }}
    >
      <Stack
        direction='row'
        sx={{
          alignItems: 'center',
          gap: '8px',
          marginRight: '8px',
        }}
      >
        {(typeof icon === 'string' ? (
          <Icon iconClass={icon} color='currentColor' />
        ) : (
          icon
        )) || null}
        {text}
      </Stack>
      {rightContent}
      {isLoadingDisabled && <Loader size='small' color='inherit' />}
      {isSubMenu && !isLoadingDisabled && (
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            '& > span': {
              height: '20px',
              padding: '0px',
              width: '20px',
            },
          }}
        >
          <Icon
            iconClass='material-symbols-chevron-right-rounded'
            color='currentColor'
          />
        </Box>
      )}
    </Stack>
  );
};
